import {
  Timeline,
  type TimelineItem,
} from "@/features/material-compliance/components/shared/Timeline"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, Bell, Navigation } from "lucide-react"

export interface Cadence {
  dueDays: number
  remindersEnabled: boolean
  intervalDays: number
  maxReminders: number
}

/** Preview of an inquiry's follow-up: deadline, reminders, hand-back. */
export function CadenceTimelinePreview({ cadence }: { cadence: Cadence }) {
  const { translate } = useTranslation()

  // The first reminder fires when the RESPONSE DEADLINE lapses (per inquiry, not a fixed
  // day here), then every interval, up to the cap, then hand-back. So the preview is
  // deadline-anchored rather than day-numbered from send.
  const items: TimelineItem[] = [
    {
      id: "deadline",
      icon: Navigation,
      tone: "success",
      title: translate("MC_TPL_TL_DEADLINE"),
      // Names the org's ACTUAL window, so the preview moves with the field above it
      // instead of describing the deadline in the abstract.
      detail: translate("MC_TPL_TL_DEADLINE_HINT", {
        days: cadence.dueDays,
      }),
    },
    ...(cadence.remindersEnabled
      ? [
          {
            id: "reminder",
            icon: Bell,
            tone: "muted" as const,
            title: translate("MC_TPL_TL_REMINDER"),
            detail: translate("MC_TPL_TL_REMINDER_LOOP", {
              interval: cadence.intervalDays,
              max: cadence.maxReminders,
            }),
          },
          {
            id: "handback",
            icon: AlertTriangle,
            tone: "yellow" as const,
            title: translate("MC_TPL_TL_ESCALATION"),
            detail: translate("MC_TPL_TL_ESCALATION_HINT"),
          },
        ]
      : [
          // Without reminders the inquiry is handed back on the deadline.
          {
            id: "handback",
            icon: AlertTriangle,
            tone: "yellow" as const,
            title: translate("SETTINGS/SUPPLIER_INQUIRIES/TL_HANDBACK"),
            detail: translate(
              "SETTINGS/SUPPLIER_INQUIRIES/TL_HANDBACK_NO_REMINDERS",
            ),
          },
        ]),
  ]

  return <Timeline items={items} />
}
